import { Box, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import {
  useGetCompanyByIdQuery,
  useUpdateCompanyByIdMutation,
} from 'store/company/company.api'
import { CompanyAssociation } from 'store/company/company.types'
import {
  DetailField,
  detailTextFieldSx,
} from '../customer-detail-fields'

interface CustomerAddressTabProps {
  association: CompanyAssociation
  onDirtyChange: (dirty: boolean) => void
  saveToken: number
  onSaved: () => void
}

const CustomerAddressTab = ({
  association,
  onDirtyChange,
  saveToken,
  onSaved,
}: CustomerAddressTabProps) => {
  const companyId = association.companyId
  const { data: company } = useGetCompanyByIdQuery({ id: companyId })
  const [updateCompany, { isLoading }] = useUpdateCompanyByIdMutation()

  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [province, setProvince] = useState('')
  const [postalCode, setPostalCode] = useState('')

  useEffect(() => {
    if (!company) return
    const line1 = [company.streetNumber, company.streetName]
      .filter(Boolean)
      .join(' ')
      .trim()
    setAddressLine1(line1)
    setAddressLine2(company.unitNumber ?? '')
    setCity(company.city ?? '')
    setProvince(company.state ?? '')
    setPostalCode(company.zipCode ?? '')
  }, [company, companyId])

  const isDirty = (() => {
    if (!company) return false
    const originalLine1 = [company.streetNumber, company.streetName]
      .filter(Boolean)
      .join(' ')
      .trim()
    return (
      addressLine1.trim() !== originalLine1 ||
      addressLine2.trim() !== (company.unitNumber ?? '').trim() ||
      city.trim() !== (company.city ?? '').trim() ||
      province.trim() !== (company.state ?? '').trim() ||
      postalCode.trim() !== (company.zipCode ?? '').trim()
    )
  })()

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (!saveToken || !company) return

    const save = async () => {
      try {
        await updateCompany({
          id: companyId,
          streetNumber: addressLine1.trim(),
          streetName: '',
          unitNumber: addressLine2.trim(),
          city: city.trim(),
          state: province.trim(),
          zipCode: postalCode.trim(),
        }).unwrap()
        onSaved()
      } catch (error) {
        console.error('Failed to update address', error)
      }
    }

    void save()
    // Intentionally keyed on saveToken only — captures form state at save time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToken])

  return (
    <Box sx={{ px: 3, py: 2, maxWidth: 560 }}>
      <DetailField label="Address Line 1">
        <TextField
          fullWidth
          size="small"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          disabled={isLoading}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Address Line 2">
        <TextField
          fullWidth
          size="small"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          disabled={isLoading}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="City">
        <TextField
          fullWidth
          size="small"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          disabled={isLoading}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Province">
        <TextField
          fullWidth
          size="small"
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          disabled={isLoading}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Postal Code">
        <TextField
          fullWidth
          size="small"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          disabled={isLoading}
          sx={detailTextFieldSx}
        />
      </DetailField>
    </Box>
  )
}

export default CustomerAddressTab
